import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getTribePost, addComment, addReaction } from "../../../data/requests/tribe-post-requests";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Send, Heart, ThumbsUp, Award, MessageCircle } from "lucide-react";
import { format, parseISO } from "date-fns";
import AuthStore from "../../../stores/AuthStore";
import { cn } from "@/lib/utils";

export function TribePostDetailScreen() {
  const { id: tribeId, postId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = AuthStore.user;
  const { t } = useTranslation();

  const [comment, setComment] = useState("");

  // Fetch post with comments
  const { data: postResponse, isLoading } = useQuery({
    queryKey: ["tribe", tribeId, "post", postId],
    queryFn: () => getTribePost(tribeId, postId),
  });

  const post = postResponse?.data?.data || postResponse?.data;

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: (content) => addComment(tribeId, postId, { content }),
    onSuccess: () => {
      setComment("");
      queryClient.invalidateQueries({ queryKey: ["tribe", tribeId, "post", postId] });
    },
  });

  // Add reaction mutation
  const addReactionMutation = useMutation({
    mutationFn: (emoji) => addReaction(tribeId, postId, emoji),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tribe", tribeId, "post", postId] });
    },
  });

  const handleSendComment = () => {
    if (!comment.trim()) return;
    addCommentMutation.mutate(comment);
  };

  const handleReaction = (emoji) => {
    addReactionMutation.mutate(emoji);
  };

  // Get reaction counts
  const getReactionCount = (emoji) => {
    return post?.reactions?.filter(r => r.emoji === emoji).length || 0;
  };

  // Check if user reacted
  const hasUserReacted = (emoji) => {
    return post?.reactions?.some(r => r.userId === user?.id && r.emoji === emoji);
  };

  // Safe date formatter
  const formatDate = (dateString) => {
    try {
      if (!dateString) return t("common.justNow", { defaultValue: "Just now" });
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
      return format(date, "MMM dd, yyyy • HH:mm");
    } catch {
      return t("common.justNow", { defaultValue: "Just now" });
    }
  };

  if (isLoading) {
    return (
      <AppLayout 
        title={t("tribes.detail.posts.title", { defaultValue: "Post" })} 
        loading={true}
      />
    );
  }

  if (!post) {
    return (
      <AppLayout title={t("tribes.detail.posts.notFound", { defaultValue: "Post not found" })}>
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <p className="text-muted-foreground">{t("tribes.detail.posts.notFound", { defaultValue: "Post not found" })}</p>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("common.back", { defaultValue: "Go Back" })}
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={post.title || t("tribes.detail.posts.title", { defaultValue: "Post" })}
      description={t("tribes.detail.posts.detailDescription", { 
        author: post.author?.name || t("common.unknown", { defaultValue: "Unknown" }),
        date: formatDate(post.createdAt)
      })}
      actions={[
        {
          name: t("common.back", { defaultValue: "Back" }),
          icon: <ArrowLeft className="h-4 w-4" />,
          onClick: () => navigate(-1),
        },
      ]}
    >
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Post Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="h-10 w-10">
                <AvatarFallback>{post.author?.name?.charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium">{post.author?.name || t("common.unknown", { defaultValue: "Unknown" })}</p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(post.createdAt)}
                </p>
              </div>
            </div>
            <CardTitle className="text-2xl">{post.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-base whitespace-pre-wrap leading-relaxed">
              {post.content}
            </p>
          </CardContent>
          <CardFooter className="border-t pt-4">
            <div className="flex items-center gap-2 w-full">
              {/* Reactions */}
              <Button
                variant={hasUserReacted('👍') ? "default" : "ghost"}
                size="sm"
                className={cn(
                  "gap-2",
                  hasUserReacted('👍') && "bg-blue-500/10 text-blue-500 hover:bg-blue-500/20"
                )}
                onClick={() => handleReaction('👍')}
              >
                <ThumbsUp className="h-4 w-4" />
                <span className="text-sm font-medium">{getReactionCount('👍')}</span>
              </Button>

              <Button
                variant={hasUserReacted('❤️') ? "default" : "ghost"}
                size="sm"
                className={cn(
                  "gap-2",
                  hasUserReacted('❤️') && "bg-red-500/10 text-red-500 hover:bg-red-500/20"
                )}
                onClick={() => handleReaction('❤️')}
              >
                <Heart className="h-4 w-4" />
                <span className="text-sm font-medium">{getReactionCount('❤️')}</span>
              </Button>

              <Button
                variant={hasUserReacted('🎉') ? "default" : "ghost"}
                size="sm"
                className={cn(
                  "gap-2",
                  hasUserReacted('🎉') && "bg-amber-500/10 text-amber-500 hover:bg-amber-500/20"
                )}
                onClick={() => handleReaction('🎉')}
              >
                <Award className="h-4 w-4" />
                <span className="text-sm font-medium">{getReactionCount('🎉')}</span>
              </Button>

              <div className="flex items-center gap-1.5 ml-auto text-sm text-muted-foreground">
                <MessageCircle className="h-4 w-4" />
                <span>{post._count?.comments || 0}</span>
              </div>
            </div>
          </CardFooter>
        </Card>

        {/* Comments Section */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">
            {t("tribes.detail.posts.comments", { 
              count: post._count?.comments || 0,
              defaultValue: `Comments (${post._count?.comments || 0})`
            })}
          </h2>

          {/* Comment Input */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarFallback>{user?.name?.charAt(0).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1 flex gap-2">
                  <Textarea
                    placeholder={t("tribes.detail.posts.commentPlaceholder", { defaultValue: "Write a comment..." })}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="resize-none"
                    rows={2}
                    maxLength={500}
                  />
                  <Button
                    size="icon"
                    onClick={handleSendComment}
                    disabled={!comment.trim() || addCommentMutation.isPending}
                    className="shrink-0"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Comments List */}
          {post.comments && post.comments.length > 0 ? (
            <div className="space-y-3">
              {post.comments.map((comment) => (
                <Card key={comment.id}>
                  <CardContent className="pt-6">
                    <div className="flex gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>{comment.author?.name?.charAt(0).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-medium">{comment.author?.name || t("common.unknown", { defaultValue: "Unknown" })}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(comment.createdAt)}
                          </p>
                        </div>
                        <p className="text-sm leading-relaxed">{comment.content}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50 text-muted-foreground" />
                <p className="text-muted-foreground">{t("tribes.detail.posts.noComments", { defaultValue: "No comments yet" })}</p>
                <p className="text-sm text-muted-foreground">{t("tribes.detail.posts.beFirst", { defaultValue: "Be the first to comment!" })}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

export default TribePostDetailScreen;
